import { Typography, useTheme } from '@mui/material';
import {
  type GridColumns,
  type GridValidRowModel,
  type GridSortModel,
} from '@mui/x-data-grid';
import Tooltip from '@mui/material/Tooltip';
import { Fragment, useEffect, useState } from 'react';
import { getAllOrganizationEmployeeWithRoleName } from 'service/UserService';
import { colorTheme } from '../../../../theme';
import { CustomDataGrid } from 'components';
import { type IEmployeeProps } from 'types';
import { useSelector, useDispatch } from 'react-redux';
import { type RootState } from 'store';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { checkSortingValues } from 'utils/GeneralUtil';
import { strings } from 'i18n/Strings';
type GridRowData = Record<string, unknown>;

const TotalEmployees = (): JSX.Element => {
  const { orgId } = useSelector((state: RootState) => state.onboarding);
  const currentTheme = useTheme();
  const colors = colorTheme(currentTheme.palette.mode);
  const [employeeData, setEmployeeData] = useState<IEmployeeProps[]>([]);
  const pageInfo = useSelector((state) => state?.pagination?.employees);
  const [pageCount, setPageCount] = useState(null);
  const dispatch = useDispatch();
  const getOrgEmployeeData = (): void => {
    getAllOrganizationEmployeeWithRoleName({
      id: orgId.toString(),
      page: Number(pageInfo?.page) + 1,
      size: pageInfo?.pageSize,
      sortBy: pageInfo?.sortBy,
      sortOrder: pageInfo?.sortOrder,
    })
      .then((res) => {
        if (typeof res !== 'undefined' && 'basicInfo' in res) {
          const { basicInfo } = res as { basicInfo: IEmployeeProps[] };
          if (basicInfo) {
            setEmployeeData(basicInfo?.rows);
            setPageCount(basicInfo?.count);
          }
        }
      })
      .catch((err: Error) => {
        throw err;
      });
  };

  useEffect(() => {
    getOrgEmployeeData();
  }, [pageInfo.pageSize, pageInfo?.sortBy, pageInfo?.sortOrder]);

  const handleSortChange = (model: GridSortModel): void => {
    dispatch(
      setSortBy({
        key: 'employees',
        value: model[0].field,
      }),
    );
    dispatch(setSortOrder({ key: 'employees', value: model[0].sort }));
    dispatch(setCurrentPage({ key: 'employees', value: 0 }));
  };
  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'firstName',
      flex: 0.5,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.firstName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.firstName} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.firstName}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'lastName',
      flex: 0.5,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.lastName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.lastName} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.lastName}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'email',
      flex: 0.7,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.emailAddress}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.email} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.email}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'phone',
      flex: 0.5,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.phoneNumber}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.phone} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.phone}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'employerContribution',
      flex: 0.5,
      headerName: 'Employer contribution',

      renderCell: ({ row }) => (
        <Tooltip title={row.employerContribution} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.employerContribution}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'employeeContribution',
      flex: 0.5,
      headerName: 'Employee contribution',
      renderCell: ({ row }) => (
        <Tooltip title={row.employeeContribution} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.employeeContribution}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  return (
    <Fragment>
      <CustomDataGrid
        // title="Employee"
        columns={columns}
        rows={employeeData as GridRowData[]}
        getRowId={(row: IEmployeeProps) =>
          `${String(row.empId)}${row.email ? row.email : ''}`
        }
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        currentPage="employees"
        pageCount={pageCount}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode) => {
          handleSortChange(mode);
        }}
      />
    </Fragment>
  );
};

export default TotalEmployees;
